export type ManageTranslationsConfig = {
	fields?: Array<{
		path: string;
		label: string;
		placeholder?: string;
	}>;
};

export type TranslationFormRow = {
	fieldLabel: string;
	placeholder?: string;
	id: string;
	/**
	 * The previously-saved translation value for this field, loaded from the backend
	 * via `layoutTranslations`. `undefined` when nothing has been stored yet.
	 */
	initialValue?: string;
	/**
	 * The stable identifier of the source array item this row belongs to (e.g. a
	 * carousel card's `cardId`), when the row was derived from an array field. Used to
	 * scope the manage-translations view to a single item. `undefined` for scalar
	 * (extension-level) rows.
	 */
	itemId?: string;
};

/**
 * Keys checked, in order, to derive a stable identifier for an array item so that
 * translation rows can be scoped to a single item independently of its position.
 */
const ITEM_ID_KEYS = ['cardId', 'id'] as const;

const getItemId = (item: Record<string, unknown>): string | undefined => {
	for (const key of ITEM_ID_KEYS) {
		const candidate = item[key];
		if (typeof candidate === 'string' && candidate.length > 0) {
			return candidate;
		}
	}

	return undefined;
};

const camelCaseToWords = (value: string): string =>
	value
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/[_-]+/g, ' ')
		.trim();

const titleCase = (value: string): string =>
	value
		.split(' ')
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');

const humanizePathSegment = (value: string): string => titleCase(camelCaseToWords(value));

const getValueByPath = (value: unknown, path: string): unknown => {
	if (!path) {
		return value;
	}

	return path.split('.').reduce<unknown>((currentValue, segment) => {
		if (!currentValue || typeof currentValue !== 'object') {
			return undefined;
		}

		return (currentValue as Record<string, unknown>)[segment];
	}, value);
};

const getFieldLabel = ({ path }: { path: string }): string => {
	const pathWithoutArrays = path.replace(/\[\]/g, '');
	const lastSegment = pathWithoutArrays.split('.').filter(Boolean).pop() ?? pathWithoutArrays;
	return humanizePathSegment(lastSegment);
};

const isStringValue = (value: unknown): value is string => typeof value === 'string';

/**
 * Builds the list of translatable form rows from the extension parameters and the
 * UI-provided field configuration.
 *
 * Rows are derived from `config.fieldLabels` (the set of translatable field paths) and
 * the current source values in `parameters`. When `storedFieldValues` is provided (a
 * map of `fieldPath -> storedValue` for a specific locale and localId), each row's
 * `initialValue` is populated from it so the editor can pre-fill previously-saved
 * translations.
 */
export const buildTranslationRows = <TParameters extends Record<string, unknown>>({
	config,
	parameters,
	itemId,
	storedFieldValues,
}: {
	config?: ManageTranslationsConfig;
	itemId?: string;
	parameters: TParameters;
	/**
	 * Pre-existing translation values for a given (locale, localId) pair, keyed by
	 * concrete fieldPath (e.g. `"cards.card-abc.title"`). When provided, matching rows
	 * will have their `initialValue` set from this map instead of being left blank.
	 */
	storedFieldValues?: Record<string, string>;
}): TranslationFormRow[] => {
	return (
		config?.fields?.reduce<TranslationFormRow[]>((accumulator, field) => {
			const path = field.path;
			const arrayMarkerIndex = path.indexOf('[].');

			if (arrayMarkerIndex >= 0) {
				const arrayKey = path.slice(0, arrayMarkerIndex);
				const itemRelativePath = path.slice(arrayMarkerIndex + '[].'.length);
				const items = getValueByPath(parameters, arrayKey);

				if (!Array.isArray(items)) {
					return accumulator;
				}

				items.forEach((item, index) => {
					if (!item || typeof item !== 'object') {
						return;
					}

					const itemRecord = item as Record<string, unknown>;

					if (itemId !== undefined && getItemId(itemRecord) !== itemId) {
						return;
					}

					const currentItemId = getItemId(itemRecord);
					// Build the concrete fieldPath that the BE uses for this array item:
					// registry path "cards[].title" + cardId "card-abc" -> "cards.card-abc.title"
					const concreteFieldPath = currentItemId
						? `${arrayKey}.${currentItemId}.${itemRelativePath}`
						: undefined;

					const rowId = `${arrayKey}.${index}.${itemRelativePath}`;

					const fieldLabel = field.label ?? getFieldLabel({ path });

					accumulator.push({
						fieldLabel,
						placeholder: field.placeholder,
						id: rowId,
						initialValue: concreteFieldPath ? storedFieldValues?.[concreteFieldPath] : undefined,
						itemId: currentItemId,
					});
				});

				return accumulator;
			}

			// Scalar fields belong to the extension level, not a specific array item.
			// Exclude them when the view is scoped to a particular item.
			if (itemId !== undefined) {
				return accumulator;
			}

			const sourceValue = getValueByPath(parameters, path);
			if (!isStringValue(sourceValue)) {
				return accumulator;
			}

			// We don't want to translate fields that are empty in default layout.
			if (!sourceValue?.trim()) {
				return accumulator;
			}

			const fieldLabel = field.label ?? getFieldLabel({ path });

			accumulator.push({
				fieldLabel,
				placeholder: field.placeholder ?? '',
				id: path,
				initialValue: storedFieldValues?.[path],
			});

			return accumulator;
		}, []) ?? []
	);
};
